import { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import Button from '../components/ui/Button'
import Drawer from '../components/ui/Drawer'
import { useToast } from '../components/Toast'
import {
  listAlerts, acknowledgeAlert, resolveAlert, reopenAlert, downloadAlertEvidence,
  markAlertFalsePositive,
} from '../api/tankguard'
import { timeAgo } from '../utils/events'

// Alert types that represent an active loss/attack — these get the evidence
// PDF and lead the queue visually.
const THEFT_TYPES = new Set(['theft', 'correlated_theft', 'tamper'])

// 0-255 acoustic measurement confidence → label + colour band. High = the
// sensor was very sure of the reading behind the alert.
function confidenceMeta(score) {
  if (score == null) return null
  const pct = Math.round((score / 255) * 100)
  const band = score >= 192
    ? { label: 'High', color: 'var(--tg-success-700, #15803d)', bg: '#f0fdf4', bd: '#bbf7d0' }
    : score >= 96
      ? { label: 'Medium', color: '#92400e', bg: '#fffbeb', bd: '#fde68a' }
      : { label: 'Low', color: 'var(--tg-danger-700)', bg: 'var(--tg-danger-50)', bd: 'var(--tg-danger-100)' }
  return { score, pct, ...band }
}

// Fusion verdict (HIGH/MEDIUM/LOW) → colour. Mirrors confidence banding.
const VERDICT_COLORS = {
  HIGH:   { color: 'var(--tg-danger-700)',            bg: 'var(--tg-danger-50)', bd: 'var(--tg-danger-100)' },
  MEDIUM: { color: '#92400e',                         bg: '#fffbeb',             bd: '#fde68a' },
  LOW:    { color: 'var(--tg-text-secondary)',        bg: 'var(--tg-bg-muted, #f3f4f6)', bd: 'var(--tg-border-default)' },
}

// Per-type presentation. `key` maps to alerts.* i18n; colour is a semantic token.
const TYPE_META = {
  theft:            { key: 'theft',       accent: 'var(--tg-danger-500)',  bg: 'var(--tg-danger-50)' },
  correlated_theft: { key: 'correlated',  accent: 'var(--tg-danger-600)',  bg: 'var(--tg-danger-50)' },
  tamper:           { key: 'tamper',      accent: '#ea580c',               bg: '#fff7ed' },
  geofence:         { key: 'geofence',    accent: '#6366f1',               bg: '#eef2ff' },
  after_hours:      { key: 'afterHours',  accent: '#6366f1',               bg: '#eef2ff' },
  anomaly:          { key: 'anomaly',     accent: 'var(--tg-warning-600, #b45309)', bg: '#fffbeb' },
}

function typeLabel(t, type) {
  const meta = TYPE_META[type]
  return meta ? t('alerts.' + meta.key) : (type || t('alerts.anomaly'))
}

const STATUS_TABS = ['open', 'acknowledged', 'resolved', 'all']

function StatusBadge({ status, t }) {
  const map = {
    open:         { label: t('alerts.statusOpen'),         color: 'var(--tg-danger-700)',  bg: 'var(--tg-danger-50)',  bd: 'var(--tg-danger-100)' },
    acknowledged: { label: t('alerts.statusAcknowledged'), color: '#92400e',               bg: '#fffbeb',              bd: '#fde68a' },
    resolved:     { label: t('alerts.statusResolved'),     color: 'var(--tg-success-700, #15803d)', bg: '#f0fdf4',      bd: '#bbf7d0' },
  }
  const s = map[status] || map.open
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--tg-text-xs)', fontWeight: 600,
      color: s.color, background: s.bg, border: '1px solid ' + s.bd, borderRadius: 'var(--tg-radius-full, 999px)',
      padding: '2px 10px', whiteSpace: 'nowrap',
    }}>
      {status === 'open' && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />}
      {s.label}
    </span>
  )
}

// Compact 0-255 confidence chip. `score` is the raw acoustic score.
function ConfidencePill({ score }) {
  const m = confidenceMeta(score)
  if (!m) return null
  return (
    <span
      title={`Sensor measurement confidence: ${m.score}/255 (${m.pct}%). The acoustic confidence the fuel sensor reported for the readings behind this alert.`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--tg-text-xs)', fontWeight: 600,
        color: m.color, background: m.bg, border: '1px solid ' + m.bd, borderRadius: 'var(--tg-radius-full, 999px)',
        padding: '2px 8px', whiteSpace: 'nowrap',
      }}
    >
      {m.score}/255
    </span>
  )
}

// One line of detail metrics for an alert (litres / €, coordinates, etc.).
function detailSummary(a, t) {
  const d = a.details || {}
  const bits = []
  if (d.fuel_drop_l != null) bits.push('−' + Math.round(d.fuel_drop_l) + ' L')
  if (d.cost_eur != null) bits.push('€' + d.cost_eur)
  if (d.speed_kmh != null) bits.push(Math.round(d.speed_kmh) + ' km/h')
  if (d.fence) bits.push(d.fence)
  if (d.total_liters != null) bits.push('−' + Math.round(d.total_liters) + ' L · ' + (d.truck_count || '?') + ' ' + t('fleet.trucks'))
  return bits.join(' · ')
}

export default function Alerts({ onReloadCounts, openAlertId, onOpened }) {
  const { t } = useTranslation()
  const toast = useToast()
  const [status, setStatus] = useState('open')
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [selected, setSelected] = useState(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [downloading, setDownloading] = useState(false)
  // Alert id we've been asked to open (deep-link from a push tap), held until
  // the matching row is present in the loaded list.
  const [pendingDeepLink, setPendingDeepLink] = useState(null)

  const load = useCallback(async () => {
    try {
      const data = await listAlerts(status === 'all' ? {} : { status })
      setAlerts(data)
      setErr('')
    } catch (e) {
      console.error(e); Sentry.captureException(e); setErr(e.message)
    }
    setLoading(false)
  }, [status])

  useEffect(() => { setLoading(true); load() }, [load])

  // A push tap handed us an alert id — widen the filter to 'all' so a resolved
  // or acknowledged alert isn't hidden, stash the id, and consume the prop so it
  // can't re-fire. The row is opened by the effect below once the list reloads.
  useEffect(() => {
    if (!openAlertId) return
    setPendingDeepLink(String(openAlertId))
    setStatus('all')
    onOpened?.()
  }, [openAlertId, onOpened])

  // Once alerts (re)load, open the pending deep-linked row if it's now present.
  useEffect(() => {
    if (!pendingDeepLink) return
    const match = alerts.find(a => String(a.id) === pendingDeepLink)
    if (match) {
      setSelected(match)
      setNote('')
      setPendingDeepLink(null)
    }
  }, [alerts, pendingDeepLink])

  // Keep the open drawer's data fresh after a reload (e.g. a live update).
  const current = useMemo(
    () => (selected ? alerts.find(a => a.id === selected.id) || selected : null),
    [selected, alerts]
  )

  async function act(fn, successMsg) {
    setBusy(true)
    try {
      await fn()
      toast.success(successMsg)
      await load()
      onReloadCounts?.()
    } catch (e) {
      Sentry.captureException(e); toast.error(e.message)
    }
    setBusy(false)
  }

  function openDetail(a) { setSelected(a); setNote('') }

  async function onDownload(a) {
    setDownloading(true)
    try {
      await downloadAlertEvidence(a.id, 'tankguard-evidence-' + (a.truck_name || a.id))
    } catch (e) {
      Sentry.captureException(e); toast.error(e.message)
    }
    setDownloading(false)
  }

  const counts = useMemo(() => {
    const c = { open: 0, acknowledged: 0, resolved: 0 }
    alerts.forEach(a => { if (c[a.status] != null) c[a.status]++ })
    return c
  }, [alerts])

  return (
    <div className="page">
      <div className="page-head" style={{ marginBottom: 'var(--tg-space-4)' }}>
        <div>
          <h1>{t('alerts.title')}</h1>
          <p>{t('alerts.subtitle')}</p>
        </div>
      </div>

      {/* Status filter — segmented, full-width & tappable on mobile */}
      <div className="alert-tabs" role="tablist" aria-label={t('alerts.title')}>
        {STATUS_TABS.map(s => (
          <button
            key={s}
            role="tab"
            aria-selected={status === s}
            className={`alert-tab${status === s ? ' is-active' : ''}`}
            onClick={() => setStatus(s)}
          >
            {t('alerts.tab_' + s)}
          </button>
        ))}
      </div>

      {err && (
        <div style={{ background: 'var(--tg-danger-50)', border: '1px solid var(--tg-danger-100)', color: 'var(--tg-danger-700)', fontSize: 'var(--tg-text-sm)', padding: '10px 16px', borderRadius: 'var(--tg-radius-lg)', margin: 'var(--tg-space-3) 0' }}>
          {t('alerts.loadError')} {err}
        </div>
      )}

      {loading ? (
        <div className="alert-list">
          {[0, 1, 2].map(i => (
            <div key={i} className="alert-card" style={{ pointerEvents: 'none' }}>
              <div className="tg-skel tg-skel--block" style={{ width: '60%', height: 14, marginBottom: 8 }} />
              <div className="tg-skel tg-skel--block" style={{ width: '35%', height: 11 }} />
            </div>
          ))}
        </div>
      ) : alerts.length === 0 ? (
        <div className="tg-card" style={{ textAlign: 'center', padding: '48px 20px' }}>
          <div style={{ fontSize: 34, marginBottom: 10 }}>✓</div>
          <div style={{ fontWeight: 'var(--tg-weight-semibold)', color: 'var(--tg-text-primary)', fontSize: 'var(--tg-text-md)' }}>
            {status === 'open' ? t('alerts.emptyOpen') : t('alerts.emptyGeneric')}
          </div>
          <div style={{ color: 'var(--tg-text-secondary)', fontSize: 'var(--tg-text-sm)', marginTop: 6 }}>
            {t('alerts.emptyHint')}
          </div>
        </div>
      ) : (
        <div className="alert-list">
          {alerts.map(a => {
            const meta = TYPE_META[a.type] || {}
            const isTheft = THEFT_TYPES.has(a.type)
            return (
              <div
                key={a.id}
                className="alert-card"
                style={{ borderLeft: '4px solid ' + (meta.accent || 'var(--tg-border-default)') }}
                onClick={() => openDetail(a)}
              >
                <div className="alert-card__main">
                  <div className="alert-card__row1">
                    <span className="alert-card__type" style={{ color: meta.accent }}>
                      {typeLabel(t, a.type)}
                    </span>
                    <StatusBadge status={a.status} t={t} />
                    {isTheft && a.confidence_score != null && <ConfidencePill score={a.confidence_score} />}
                    {a.false_positive && (
                      <span style={{ fontSize: 'var(--tg-text-xs)', fontWeight: 600, color: 'var(--tg-text-muted)', background: 'var(--tg-bg-muted, #f3f4f6)', border: '1px solid var(--tg-border-default)', borderRadius: 'var(--tg-radius-full, 999px)', padding: '2px 8px' }}>
                        {t('alerts.falsePositiveTag')}
                      </span>
                    )}
                  </div>
                  <div className="alert-card__truck">{a.truck_name || t('alerts.unknownTruck')}</div>
                  <div className="alert-card__meta">
                    {detailSummary(a, t)}
                    {detailSummary(a, t) && ' · '}
                    <span style={{ color: 'var(--tg-text-muted)' }}>{timeAgo(new Date(a.occurred_at).getTime())}</span>
                  </div>
                </div>
                <div className="alert-card__actions" onClick={e => e.stopPropagation()}>
                  {a.status === 'open' && (
                    <Button size="sm" variant="secondary" disabled={busy}
                      onClick={() => act(() => acknowledgeAlert(a.id), t('alerts.ackDone'))}>
                      {t('alerts.acknowledge')}
                    </Button>
                  )}
                  {a.status !== 'resolved' && (
                    <Button size="sm" variant="primary" disabled={busy}
                      onClick={() => act(() => resolveAlert(a.id), t('alerts.resolveDone'))}>
                      {t('alerts.resolve')}
                    </Button>
                  )}
                  {isTheft && (
                    <Button size="sm" variant="ghost" disabled={downloading}
                      onClick={() => onDownload(a)}>
                      {t('alerts.evidence')}
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Detail drawer */}
      <Drawer open={!!current} onClose={() => setSelected(null)} title={current ? typeLabel(t, current.type) : ''} width={440}>
        {current && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--tg-space-4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <StatusBadge status={current.status} t={t} />
              <span style={{ fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-secondary)' }}>
                {new Date(current.occurred_at).toLocaleString()}
              </span>
            </div>

            <div>
              <div style={{ fontSize: 'var(--tg-text-lg, 18px)', fontWeight: 700, color: 'var(--tg-text-primary)' }}>
                {current.truck_name || t('alerts.unknownTruck')}
              </div>
            </div>

            {/* Detail grid */}
            <div className="alert-detail-grid">
              {current.details?.fuel_drop_l != null && (
                <Field label={t('alerts.litresLost')} value={'−' + Math.round(current.details.fuel_drop_l) + ' L'} />
              )}
              {current.details?.cost_eur != null && (
                <Field label={t('alerts.valueLost')} value={'€' + current.details.cost_eur} strong />
              )}
              {current.details?.tank && (
                <Field label={t('alerts.tank')} value={current.details.tank === 'L' ? t('alerts.tankLeft') : current.details.tank === 'R' ? t('alerts.tankRight') : current.details.tank} />
              )}
              {current.details?.speed_kmh != null && (
                <Field label={t('alerts.speed')} value={Math.round(current.details.speed_kmh) + ' km/h'} />
              )}
              {current.details?.fence && (
                <Field label={t('alerts.geofence')} value={current.details.fence} />
              )}
              {current.details?.lat != null && current.details?.lng != null && (
                <Field
                  label={t('alerts.location')}
                  value={Number(current.details.lat).toFixed(5) + ', ' + Number(current.details.lng).toFixed(5)}
                />
              )}
            </div>

            {/* Detection panel — fusion verdict + confidence + plain-language rationale */}
            {THEFT_TYPES.has(current.type) && (current.confidence_score != null || current.verdict || current.rationale) && (
              <div style={{ border: '1px solid var(--tg-border-default)', borderRadius: 'var(--tg-radius-lg)', padding: 'var(--tg-space-3)', background: 'var(--tg-bg-muted, #f9fafb)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: current.rationale ? 8 : 0, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 'var(--tg-text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--tg-text-tertiary)', fontWeight: 600 }}>
                    {t('alerts.detection')}
                  </span>
                  {current.verdict && (() => {
                    const vc = VERDICT_COLORS[current.verdict] || VERDICT_COLORS.LOW
                    return (
                      <span style={{ fontSize: 'var(--tg-text-xs)', fontWeight: 700, color: vc.color, background: vc.bg, border: '1px solid ' + vc.bd, borderRadius: 'var(--tg-radius-full, 999px)', padding: '2px 10px' }}>
                        {t('alerts.verdict_' + current.verdict.toLowerCase(), current.verdict)}
                      </span>
                    )
                  })()}
                  {current.confidence_score != null && <ConfidencePill score={current.confidence_score} />}
                </div>
                {current.rationale && (
                  <div style={{ fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-primary)', lineHeight: 1.5 }}>
                    {current.rationale}
                  </div>
                )}
              </div>
            )}

            {/* Workflow trail */}
            <div style={{ borderTop: '1px solid var(--tg-border-default)', paddingTop: 'var(--tg-space-3)', fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-secondary)', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {current.acknowledged_at && (
                <div>{t('alerts.acknowledgedBy')} <b>{current.acknowledged_by_name || '—'}</b> · {new Date(current.acknowledged_at).toLocaleString()}</div>
              )}
              {current.resolved_at && (
                <div>{t('alerts.resolvedBy')} <b>{current.resolved_by_name || '—'}</b> · {new Date(current.resolved_at).toLocaleString()}</div>
              )}
              {current.resolution_note && (
                <div style={{ fontStyle: 'italic', color: 'var(--tg-text-primary)' }}>“{current.resolution_note}”</div>
              )}
            </div>

            {THEFT_TYPES.has(current.type) && (
              <Button variant="secondary" fullWidth disabled={downloading} onClick={() => onDownload(current)}>
                {downloading ? t('common.loading') : t('alerts.downloadEvidence')}
              </Button>
            )}

            {THEFT_TYPES.has(current.type) && !current.false_positive && (
              <Button variant="ghost" fullWidth disabled={busy}
                onClick={() => act(() => markAlertFalsePositive(current.id), t('alerts.falsePositiveDone'))}>
                {t('alerts.markFalsePositive')}
              </Button>
            )}

            {/* Actions */}
            {current.status !== 'resolved' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--tg-space-3)', borderTop: '1px solid var(--tg-border-default)', paddingTop: 'var(--tg-space-4)' }}>
                {current.status === 'open' && (
                  <Button variant="secondary" fullWidth disabled={busy}
                    onClick={() => act(() => acknowledgeAlert(current.id), t('alerts.ackDone'))}>
                    {t('alerts.acknowledge')}
                  </Button>
                )}
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder={t('alerts.resolutionPlaceholder')}
                  rows={3}
                  style={{ width: '100%', fontFamily: 'inherit', fontSize: 'var(--tg-text-sm)', padding: '10px 12px', borderRadius: 'var(--tg-radius-lg)', border: '1px solid var(--tg-border-default)', resize: 'vertical', minHeight: 44, boxSizing: 'border-box' }}
                />
                <Button variant="primary" fullWidth disabled={busy}
                  onClick={() => act(() => resolveAlert(current.id, note), t('alerts.resolveDone'))}>
                  {t('alerts.markResolved')}
                </Button>
              </div>
            ) : (
              <div style={{ borderTop: '1px solid var(--tg-border-default)', paddingTop: 'var(--tg-space-4)' }}>
                <Button variant="ghost" fullWidth disabled={busy}
                  onClick={() => act(() => reopenAlert(current.id), t('alerts.reopenDone'))}>
                  {t('alerts.reopen')}
                </Button>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  )
}

function Field({ label, value, strong }) {
  return (
    <div className="alert-detail-field">
      <div className="alert-detail-field__label">{label}</div>
      <div className="alert-detail-field__value" style={strong ? { color: 'var(--tg-danger-700)', fontWeight: 700 } : undefined}>{value}</div>
    </div>
  )
}
